import { useState, useEffect, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import debounce from 'lodash.debounce';
import memberService from '../../services/memberService';
import dayjs from 'dayjs';

// Material UI imports
import {
  Box,
  Paper,
  Grid,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  CircularProgress,
  Divider,
  Tooltip,
  IconButton,
  Alert,
  Card,
  CardContent,
  Tabs,
  Tab,
  TablePagination,
  Autocomplete,
  Backdrop,
  ListItemText,
  Breadcrumbs,
  Link as MuiLink,
  Menu,
  Avatar,
  Container
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

// Charts
import { 
  PieChart, 
  Pie, 
  Cell, 
  Tooltip as RechartsTooltip, 
  Legend,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid
} from 'recharts';

// Icons
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  Assessment as AssessmentIcon,
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Done as DoneIcon,
  MoreVert as MoreVertIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';

const AdminDebtManagementPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);
  
  // Data states
  const [members, setMembers] = useState([]);
  const [allDebts, setAllDebts] = useState([]);
  const [selectedDebts, setSelectedDebts] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [debtAnalytics, setDebtAnalytics] = useState(null);
  const [paymentAnalytics, setPaymentAnalytics] = useState(null);
  const [categoryDistribution, setCategoryDistribution] = useState([]);
  const [allPayments, setAllPayments] = useState([]);
  const [loading, setLoading] = useState({
    members: false,
    debts: false,
    analytics: false,
    action: false,
    payments: false
  });
  
  // Filters and pagination
  const [filters, setFilters] = useState({
    search: '',
    status: 'all',
    category: 'all',
    dateRange: null
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Dialog states
  const [bulkAddDialog, setBulkAddDialog] = useState(false);
  const [bulkEditDialog, setBulkEditDialog] = useState(false);
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null);
  
  // Forms
  const bulkAddForm = useForm({
    defaultValues: {
      amount: '',
      category: 'dues',
      description: '',
      dueDate: dayjs().add(30, 'days'),
      applyToAll: false,
    }
  });
  
  const bulkEditForm = useForm({
    defaultValues: {
      status: 'active',
      newDueDate: null,
      applyDueDateToAll: false,
    }
  });

  // Load data
  const fetchMembers = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, members: true }));
      const response = await memberService.getAllMembers(1, 1000);
      setMembers(response.data || []);
    } catch (error) {
      enqueueSnackbar(`Failed to load members: ${error.message}`, { variant: 'error' });
    } finally {
      setLoading(prev => ({ ...prev, members: false }));
    }
  }, [enqueueSnackbar]);

  const fetchDebts = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, debts: true }));
      
      const filterParams = {};
      if (filters.search) filterParams.search = filters.search;
      if (filters.status !== 'all') filterParams.status = filters.status;
      if (filters.category !== 'all') filterParams.category = filters.category;
      if (filters.dateRange) {
        filterParams.startDate = filters.dateRange.startDate;
        filterParams.endDate = filters.dateRange.endDate;
      }
      
      const response = await memberService.getAllDebts(filterParams);
      setAllDebts(response.data || []);
    } catch (error) {
      enqueueSnackbar(`Failed to load debts: ${error.message}`, { variant: 'error' });
    } finally {
      setLoading(prev => ({ ...prev, debts: false }));
    }
  }, [filters, enqueueSnackbar]);

  const fetchAllPayments = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, payments: true }));
      const response = await memberService.getAllPayments();
      if (response && response.data) {
        setAllPayments(response.data || []);
      }
    } catch (error) {
      console.error("Failed to load all payments:", error);
      enqueueSnackbar(`Could not retrieve payment data: ${error.message}`, { variant: 'warning' });
    } finally {
      setLoading(prev => ({ ...prev, payments: false }));
    }
  }, [enqueueSnackbar]);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, analytics: true }));
      
      const debtResponse = await memberService.getDebtAnalytics();
      setDebtAnalytics(debtResponse.data || {
        totalDebts: 0,
        activeDebts: 0,
        resolvedDebts: 0,
        totalAmount: 0,
        outstandingAmount: 0,
        paidAmount: 0
      });
      
      const paymentResponse = await memberService.getPaymentAnalytics();
      setPaymentAnalytics(paymentResponse.data || {
        totalPayments: 0,
        totalAmount: 0,
        byMethod: {},
        byMonth: []
      });
      
      fetchAllPayments();
      
      if (debtResponse.data?.byCategory) {
        const categories = Object.keys(debtResponse.data.byCategory);
        const distribution = categories.map(category => ({
          name: category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          value: debtResponse.data.byCategory[category].totalAmount,
          count: debtResponse.data.byCategory[category].count,
          color: getCategoryColor(category)
        }));
        setCategoryDistribution(distribution);
      }
      
    } catch (error) {
      enqueueSnackbar(`Failed to load analytics: ${error.message}`, { variant: 'error' });
    } finally {
      setLoading(prev => ({ ...prev, analytics: false }));
    }
  }, [enqueueSnackbar, fetchAllPayments]);

  useEffect(() => {
    fetchMembers();
    fetchAnalytics();
  }, [fetchMembers, fetchAnalytics]);

  useEffect(() => {
    fetchDebts();
  }, [fetchDebts]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSelectedDebts([]);
    setSelectedMembers([]);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedDebts = allDebts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSearchChange = debounce((e) => {
    setFilters(prev => ({ ...prev, search: e.target.value }));
    setPage(0);
  }, 500);

  const handleStatusFilterChange = (e) => {
    setFilters(prev => ({ ...prev, status: e.target.value }));
    setPage(0);
  };

  const handleCategoryFilterChange = (e) => {
    setFilters(prev => ({ ...prev, category: e.target.value }));
    setPage(0);
  };

  const handleSelectAllDebts = (event) => {
    if (event.target.checked) {
      setSelectedDebts(allDebts.map(debt => debt._id));
    } else {
      setSelectedDebts([]);
    }
  };

  const handleSelectDebt = (debtId) => {
    setSelectedDebts(prev => {
      if (prev.includes(debtId)) {
        return prev.filter(id => id !== debtId);
      } else {
        return [...prev, debtId];
      }
    });
  };

  const handleSelectMember = (memberId) => {
    setSelectedMembers(prev => {
      if (prev.includes(memberId)) {
        return prev.filter(id => id !== memberId);
      } else {
        return [...prev, memberId];
      }
    });
  };

  const openBulkAddDialog = () => {
    if (selectedMembers.length === 0) {
      enqueueSnackbar('Please select at least one member', { variant: 'warning' });
      return;
    }
    setBulkAddDialog(true);
  };

  const openBulkEditDialog = () => {
    if (selectedDebts.length === 0) {
      enqueueSnackbar('Please select at least one debt', { variant: 'warning' });
      return;
    }
    setBulkEditDialog(true);
  };

  const openBulkDeleteDialog = () => {
    if (selectedDebts.length === 0) {
      enqueueSnackbar('Please select at least one debt', { variant: 'warning' });
      return;
    }
    setBulkDeleteDialog(true);
  };

  const handleBulkAddSubmit = async (data) => {
    try {
      setLoading(prev => ({ ...prev, action: true }));
      
      const targetMemberIds = data.applyToAll 
        ? members.map(member => member._id.toString())
        : selectedMembers.map(id => id.toString());
      
      if (targetMemberIds.length === 0) {
        throw new Error('No members selected for debt assignment');
      }
      
      const dueDateFormatted = data.dueDate instanceof dayjs 
        ? data.dueDate.format('YYYY-MM-DD')
        : (data.dueDate instanceof Date 
            ? data.dueDate.toISOString().split('T')[0] 
            : typeof data.dueDate === 'string' ? data.dueDate : null);
      
      if (!dueDateFormatted) {
        throw new Error('Valid due date is required');
      }
      
      const debtData = {
        amount: parseFloat(data.amount),
        category: data.category,
        description: data.description || '',
        dueDate: dueDateFormatted
      };
      
      const response = await memberService.bulkAddDebts(targetMemberIds, debtData);
      
      setBulkAddDialog(false);
      bulkAddForm.reset({
        amount: '',
        category: 'dues',
        description: '',
        dueDate: dayjs().add(30, 'days'),
        applyToAll: false,
      });
      
      setSelectedMembers([]);
      
      fetchDebts();
      fetchAnalytics();
      
      const successCount = response?.data?.results?.length || targetMemberIds.length;
      const errorCount = response?.data?.errors?.length || 0;
      
      enqueueSnackbar(
        <Box>
          <Typography variant="subtitle2">Debt Assignment Successful</Typography>
          <Typography variant="body2">
            {`Successfully added ${formatCurrency(data.amount)} debt to ${successCount} members`}
            {errorCount > 0 && ` with ${errorCount} errors`}
          </Typography>
        </Box>, 
        { 
          variant: 'success',
          autoHideDuration: 5000,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          }
        }
      );
    } catch (error) {
      console.error('Failed to add debts:', error);
      
      const errorMessage = error.response?.data?.message || error.message;
      const detailedErrors = error.response?.data?.errors;
      
      enqueueSnackbar(
        <Box>
          <Typography variant="subtitle2">Debt Assignment Failed</Typography>
          <Typography variant="body2">
            {`Error: ${errorMessage}`}
            {detailedErrors && detailedErrors.length > 0 && 
              ` (${detailedErrors.length} detail${detailedErrors.length > 1 ? 's' : ''})`}
          </Typography>
        </Box>, 
        { 
          variant: 'error',
          autoHideDuration: 5000,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'right',
          }
        }
      );
    } finally {
      setLoading(prev => ({ ...prev, action: false }));
    }
  };

  const handleBulkEditSubmit = async (data) => {
    try {
      setLoading(prev => ({ ...prev, action: true }));
      
      await memberService.bulkUpdateDebtStatus(selectedDebts, data.status);
      
      if (data.applyDueDateToAll && data.newDueDate) {
        console.log('Would update due dates to:', data.newDueDate.toISOString());
      }
      
      setBulkEditDialog(false);
      bulkEditForm.reset({
        status: 'active',
        newDueDate: null,
        applyDueDateToAll: false,
      });
      
      setSelectedDebts([]);
      
      fetchDebts();
      fetchAnalytics();
      
      enqueueSnackbar(`Successfully updated ${selectedDebts.length} debts`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to update debts: ${error.message}`, { variant: 'error' });
    } finally {
      setLoading(prev => ({ ...prev, action: false }));
    }
  };

  const handleBulkDelete = async () => {
    try {
      setLoading(prev => ({ ...prev, action: true }));
      
      const response = await memberService.bulkDeleteDebts(selectedDebts);
      
      setBulkDeleteDialog(false);
      
      setSelectedDebts([]);
      
      fetchDebts();
      fetchAnalytics();
      
      enqueueSnackbar(
        <Box>
          <Typography variant="subtitle2">Debts Deleted Successfully</Typography>
          <Typography variant="body2">
            {`Successfully deleted ${selectedDebts.length} ${selectedDebts.length === 1 ? 'debt' : 'debts'}`}
          </Typography>
        </Box>,
        {
          variant: 'success',
          autoHideDuration: 4000,
          anchorOrigin: {
            vertical: 'bottom',
            horizontal: 'center',
          }
        }
      );
    } catch (error) {
      enqueueSnackbar(`Failed to delete debts: ${error.message}`, { variant: 'error' });
    } finally {
      setLoading(prev => ({ ...prev, action: false }));
    }
  };

  const handleMarkAllResolved = async () => {
    try {
      setLoading(prev => ({ ...prev, action: true }));
      
      await memberService.bulkUpdateDebtStatus(selectedDebts, 'resolved');
      
      setSelectedDebts([]);
      setActionMenuAnchor(null);
      
      fetchDebts();
      fetchAnalytics();
      
      enqueueSnackbar(
        <Box>
          <Typography variant="subtitle2">Debts Marked as Resolved</Typography>
          <Typography variant="body2">
            {`Successfully marked ${selectedDebts.length} ${selectedDebts.length === 1 ? 'debt' : 'debts'} as resolved`}
          </Typography>
        </Box>,
        {
          variant: 'success',
          autoHideDuration: 4000
        }
      );
    } catch (error) {
      enqueueSnackbar(`Failed to update debts: ${error.message}`, { variant: 'error' });
    } finally {
      setLoading(prev => ({ ...prev, action: false }));
    }
  };

  const handleMarkAllActive = async () => {
    try {
      setLoading(prev => ({ ...prev, action: true }));
      
      await memberService.bulkUpdateDebtStatus(selectedDebts, 'active');
      
      setSelectedDebts([]);
      setActionMenuAnchor(null);
      
      fetchDebts();
      fetchAnalytics();
      
      enqueueSnackbar(`Successfully marked ${selectedDebts.length} debts as active`, { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`Failed to update debts: ${error.message}`, { variant: 'error' });
    } finally {
      setLoading(prev => ({ ...prev, action: false }));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getMemberNameById = (memberId) => {
    const member = members.find(m => m._id === memberId);
    return member && member.userId 
      ? `${member.userId.firstName} ${member.userId.lastName}` 
      : 'Unknown Member';
  };

  const getCategoryColor = (category) => {
    const colorMap = {
      dues: '#4caf50',
      fees: '#2196f3',
      event_fee: '#ff9800',
      late_payment: '#f44336',
      other: '#9c27b0'
    };
    return colorMap[category] || '#757575';
  };

  const getStatusColor = (status) => {
    return status === 'active' ? 'error' : 'success';
  };

  const openActionMenu = (event) => {
    setActionMenuAnchor(event.currentTarget);
  };

  const closeActionMenu = () => {
    setActionMenuAnchor(null);
  };

  const renderAnalyticsTab = () => {
    const clientCalculatedTotal = allPayments.length > 0 ? 
      allPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0) : 
      (paymentAnalytics?.totalAmount || 0);
    
    return (
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Financial Overview
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', position: 'relative' }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.8 }}>
                      Total Income Received
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                      <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                        {paymentAnalytics ? formatCurrency(paymentAnalytics.totalAmount) : '-'}
                      </Typography>
                      {paymentAnalytics?.trend && 
                        <Tooltip title={`${paymentAnalytics.trend > 0 ? 'Increased' : 'Decreased'} from previous period`}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {paymentAnalytics.trend > 0 ? 
                              <Typography variant="body2" sx={{ color: 'success.light', display: 'flex', alignItems: 'center' }}>
                                +{paymentAnalytics.trend}% ↑
                              </Typography> : 
                              <Typography variant="body2" sx={{ color: 'error.light', display: 'flex', alignItems: 'center' }}>
                                {paymentAnalytics.trend}% ↓
                              </Typography>
                            }
                          </Box>
                        </Tooltip>
                      }
                    </Box>
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Total Payments:</span>
                        <span><b>{paymentAnalytics?.totalPayments || 0}</b></span>
                      </Typography>
                      <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Avg Per Payment:</span>
                        <span><b>{paymentAnalytics && paymentAnalytics.totalPayments > 0 ? 
                          formatCurrency(paymentAnalytics.totalAmount / paymentAnalytics.totalPayments) : '-'}</b></span>
                      </Typography>
                      {loading.payments ? (
                        <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span>Client-side Total:</span>
                          <span><CircularProgress size={12} sx={{ ml: 1 }} /></span>
                        </Typography>
                      ) : (
                        <Tooltip title={allPayments.error ? "Server error, showing API total" : "Calculated from raw payment data"}>
                          <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span>{allPayments.error ? "API Total:" : "Verified Total:"}</span>
                            <span><b>{formatCurrency(clientCalculatedTotal)}</b></span>
                          </Typography>
                        </Tooltip>
                      )}
                      <Typography variant="body2" sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span>Most Recent:</span>
                        <span><b>{paymentAnalytics?.mostRecent ? formatDate(paymentAnalytics.mostRecent) : 'N/A'}</b></span>
                      </Typography>
                    </Box>
                    <Tooltip title="View detailed payment history">
                      <Button 
                        size="small" 
                        variant="outlined"
                        sx={{ 
                          mt: 1, 
                          color: 'primary.contrastText', 
                          borderColor: 'primary.contrastText',
                          '&:hover': { 
                            borderColor: 'primary.contrastText', 
                            bgcolor: 'rgba(255,255,255,0.1)' 
                          } 
                        }}
                        onClick={() => navigate('/admin/payments')}
                      >
                        Payment Details
                      </Button>
                    </Tooltip>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ bgcolor: 'error.light', color: 'error.contrastText' }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.8 }}>
                      Outstanding Debts
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {debtAnalytics ? formatCurrency(debtAnalytics.outstandingAmount) : '-'}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      From {debtAnalytics?.activeDebts || 0} active debts
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ bgcolor: 'success.light', color: 'success.contrastText' }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ mb: 1, opacity: 0.8 }}>
                      Total Paid Off
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                      {debtAnalytics ? formatCurrency(debtAnalytics.paidAmount) : '-'}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      From {debtAnalytics?.resolvedDebts || 0} resolved debts
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Debt Category Distribution
            </Typography>
            <Box sx={{ height: 300, mt: 3 }}>
              {loading.analytics ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </Box>
              ) : categoryDistribution.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={categoryDistribution}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="value"
                      nameKey="name"
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    >
                      {categoryDistribution.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color || `#${Math.floor(Math.random()*16777215).toString(16)}`} />
                      ))}
                    </Pie>
                    <RechartsTooltip 
                      formatter={(value, name) => [formatCurrency(value), name]}
                      contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', borderRadius: 8, border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}
                    />
                    <Legend verticalAlign="bottom" height={36} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography color="text.secondary">No data available</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Payment Methods
            </Typography>
            <Box sx={{ height: 300, mt: 3 }}>
              {loading.analytics ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </Box>
              ) : paymentAnalytics?.byMethod ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={Object.entries(paymentAnalytics.byMethod).map(([method, data]) => ({
                      name: method.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                      amount: data.amount,
                      count: data.count
                    }))}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
                    <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                    <RechartsTooltip 
                      formatter={(value, name) => {
                        if (name === 'amount') return [formatCurrency(value), 'Amount'];
                        return [value, 'Count'];
                      }}
                      contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', borderRadius: 8, border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}
                    />
                    <Legend />
                    <Bar yAxisId="left" dataKey="amount" name="Amount" fill="#8884d8" />
                    <Bar yAxisId="right" dataKey="count" name="Number of Payments" fill="#82ca9d" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography color="text.secondary">No data available</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
        
        <Grid item xs={12}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Monthly Payment Trends
            </Typography>
            <Box sx={{ height: 300, mt: 3 }}>
              {loading.analytics ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </Box>
              ) : paymentAnalytics?.byMonth && paymentAnalytics.byMonth.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={paymentAnalytics.byMonth}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <RechartsTooltip 
                      formatter={(value) => [formatCurrency(value), 'Amount']}
                      contentStyle={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', borderRadius: 8, border: 'none', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}
                    />
                    <Bar dataKey="amount" fill="#3f51b5" name="Monthly Income" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <Typography color="text.secondary">No monthly data available</Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    );
  };

  const renderAllDebtsTab = () => (
    <>
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              fullWidth
              variant="outlined"
              label="Search"
              size="small"
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                value={filters.status}
                onChange={handleStatusFilterChange}
                label="Status"
              >
                <MenuItem value="all">All Statuses</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel id="category-filter-label">Category</InputLabel>
              <Select
                labelId="category-filter-label"
                value={filters.category}
                onChange={handleCategoryFilterChange}
                label="Category"
              >
                <MenuItem value="all">All Categories</MenuItem>
                <MenuItem value="dues">Membership Dues</MenuItem>
                <MenuItem value="fees">Administrative Fees</MenuItem>
                <MenuItem value="event_fee">Event Fee</MenuItem>
                <MenuItem value="late_payment">Late Payment</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Due Date"
                value={filters.dueDate || null}
                onChange={(newValue) => setFilters(prev => ({ ...prev, dueDate: newValue }))}
                slotProps={{
                  textField: {
                    size: 'small',
                    fullWidth: true,
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={12} md={2}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                setFilters({
                  search: '',
                  status: 'all',
                  category: 'all',
                  dateRange: null
                });
                setPage(0);
              }}
              fullWidth
            >
              Reset Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      <Paper sx={{ mb: 3 }}>
        <TableContainer>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6">
              All Debts {allDebts.length > 0 && `(${allDebts.length})`}
            </Typography>
            <Box>
              <Button
                variant="contained"
                color="error"
                startIcon={<DeleteIcon />}
                disabled={selectedDebts.length === 0}
                onClick={openBulkDeleteDialog}
                sx={{ mr: 1 }}
              >
                Delete Selected
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<EditIcon />}
                disabled={selectedDebts.length === 0}
                onClick={openBulkEditDialog}
                sx={{ mr: 1 }}
              >
                Edit Selected
              </Button>
              <Button
                variant="contained"
                aria-controls="debt-action-menu"
                aria-haspopup="true"
                onClick={openActionMenu}
                disabled={selectedDebts.length === 0}
                endIcon={<MoreVertIcon />}
              >
                Actions
              </Button>
              <Menu
                id="debt-action-menu"
                anchorEl={actionMenuAnchor}
                keepMounted
                open={Boolean(actionMenuAnchor)}
                onClose={closeActionMenu}
              >
                <MenuItem onClick={handleMarkAllResolved}>
                  <ListItemText primary="Mark as Resolved" />
                  <CheckCircleIcon fontSize="small" sx={{ ml: 1, color: 'success.main' }} />
                </MenuItem>
                <MenuItem onClick={handleMarkAllActive}>
                  <ListItemText primary="Mark as Active" />
                  <CancelIcon fontSize="small" sx={{ ml: 1, color: 'error.main' }} />
                </MenuItem>
              </Menu>
            </Box>
          </Box>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedDebts.length > 0 && selectedDebts.length < allDebts.length}
                    checked={allDebts.length > 0 && selectedDebts.length === allDebts.length}
                    onChange={handleSelectAllDebts}
                  />
                </TableCell>
                <TableCell>Member</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading.debts ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} sx={{ mr: 1 }} />
                    Loading debts...
                  </TableCell>
                </TableRow>
              ) : paginatedDebts.length > 0 ? (
                paginatedDebts.map((debt) => (
                  <TableRow key={debt._id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedDebts.includes(debt._id)}
                        onChange={() => handleSelectDebt(debt._id)}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                        {getMemberNameById(debt.memberId)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {debt.memberId}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={debt.category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        size="small"
                        sx={{
                          bgcolor: `${getCategoryColor(debt.category)}20`,
                          color: getCategoryColor(debt.category),
                          fontWeight: 'medium'
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 'medium', color: debt.status === 'active' ? 'error.main' : 'text.primary' }}>
                        {formatCurrency(debt.amount)}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(debt.dueDate)}</TableCell>
                    <TableCell>
                      <Chip
                        label={debt.status.toUpperCase()}
                        color={getStatusColor(debt.status)}
                        size="small"
                        sx={{ fontWeight: 'medium' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={debt.description || 'No description'}>
                        <Typography variant="body2" sx={{ maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {debt.description || 'No description'}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title="View Member">
                        <IconButton 
                          size="small"
                          onClick={() => navigate(`/admin/members/${debt.memberId}`)}
                        >
                          <PersonAddIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={debt.status === 'active' ? 'Mark as Resolved' : 'Mark as Active'}>
                        <IconButton
                          size="small"
                          onClick={async () => {
                            try {
                              await memberService.updateDebtStatus(
                                debt.memberId, 
                                debt._id, 
                                debt.status === 'active' ? 'resolved' : 'active'
                              );
                              fetchDebts();
                              fetchAnalytics();
                              enqueueSnackbar(`Debt marked as ${debt.status === 'active' ? 'resolved' : 'active'}`, { variant: 'success' });
                            } catch (error) {
                              enqueueSnackbar(`Failed to update debt: ${error.message}`, { variant: 'error' });
                            }
                          }}
                          color={debt.status === 'active' ? 'success' : 'warning'}
                        >
                          {debt.status === 'active' ? <CheckCircleIcon /> : <CancelIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={async () => {
                            try {
                              await memberService.deleteDebt(debt.memberId, debt._id);
                              fetchDebts();
                              fetchAnalytics();
                              enqueueSnackbar('Debt deleted successfully', { variant: 'success' });
                            } catch (error) {
                              enqueueSnackbar(`Failed to delete debt: ${error.message}`, { variant: 'error' });
                            }
                          }}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    No debts found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={allDebts.length}
            page={page}
            onPageChange={handlePageChange}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]}
          />
        </TableContainer>
      </Paper>
    </>
  );

  const renderBulkAssignTab = () => (
    <>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Bulk Assign Debts to Members
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Select members from the list below, then click "Assign Debt" to add the same debt to all selected members.
          </Alert>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <Autocomplete
                multiple
                id="member-selector"
                options={members}
                loading={loading.members}
                getOptionLabel={(option) => 
                  option.userId 
                    ? `${option.userId.firstName} ${option.userId.lastName}`
                    : 'Unknown Member'
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Search Members"
                    placeholder="Type to search members"
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <>
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                          {params.InputProps.startAdornment}
                        </>
                      ),
                      endAdornment: (
                        <>
                          {loading.members ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                onChange={(event, newValue) => {
                  setSelectedMembers(newValue.map(member => member._id));
                }}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                      <Avatar sx={{ width: 28, height: 28, mr: 1, bgcolor: 'primary.main' }}>
                        {option.userId?.firstName?.[0] || '?'}
                      </Avatar>
                      <Box>
                        <Typography variant="body2">
                          {option.userId ? `${option.userId.firstName} ${option.userId.lastName}` : 'Unknown Member'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.membershipNumber || 'No membership number'}
                        </Typography>
                      </Box>
                    </Box>
                  </li>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openBulkAddDialog}
                disabled={selectedMembers.length === 0}
              >
                Assign Debt to Selected Members ({selectedMembers.length})
              </Button>
            </Grid>
          </Grid>
        </Box>
        
        <Divider sx={{ my: 3 }} />
        
        <Typography variant="subtitle1" gutterBottom>
          Selected Members ({selectedMembers.length})
        </Typography>
        
        {selectedMembers.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Membership Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members
                  .filter(member => selectedMembers.includes(member._id))
                  .map(member => (
                    <TableRow key={member._id} hover>
                      <TableCell>
                        {member.userId 
                          ? `${member.userId.firstName} ${member.userId.lastName}` 
                          : 'Unknown Member'}
                      </TableCell>
                      <TableCell>{member.userId?.email || 'No email'}</TableCell>
                      <TableCell>{member.membershipType || 'Standard'}</TableCell>
                      <TableCell>
                        <Chip
                          label={member.status?.toUpperCase() || 'UNKNOWN'}
                          size="small"
                          color={member.status === 'active' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Remove from selection">
                          <IconButton
                            size="small"
                            onClick={() => handleSelectMember(member._id)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View Member">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/admin/members/${member._id}`)}
                          >
                            <PersonAddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography color="text.secondary">
              No members selected
            </Typography>
          </Box>
        )}
      </Paper>
    </>
  );

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 1, md: 4 }, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 1 }}>
          <MuiLink color="inherit" component={Link} to="/admin/dashboard">
            Dashboard
          </MuiLink>
          <Typography color="textPrimary">Debt Management</Typography>
        </Breadcrumbs>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4">
            Debt Management & Financial Analytics
          </Typography>
          <Button
            variant="contained"
            startIcon={<AttachMoneyIcon />}
            color="primary"
            onClick={() => setTabValue(2)}
          >
            Bulk Assign Debts
          </Button>
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Financial Analytics" />
          <Tab label="All Debts" />
          <Tab label="Bulk Assign" />
        </Tabs>
      </Box>

      <Box sx={{ mb: 3 }}>
        {tabValue === 0 && renderAnalyticsTab()}
        {tabValue === 1 && renderAllDebtsTab()}
        {tabValue === 2 && renderBulkAssignTab()}
      </Box>
      
      <Dialog open={bulkAddDialog} onClose={() => setBulkAddDialog(false)} maxWidth="sm" fullWidth>
        <form onSubmit={bulkAddForm.handleSubmit(handleBulkAddSubmit)}>
          <DialogTitle>
            Assign Debt to {selectedMembers.length} Member{selectedMembers.length !== 1 && 's'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                  {...bulkAddForm.register('amount', { 
                    required: 'Amount is required',
                    min: { value: 0.01, message: 'Amount must be greater than zero' }
                  })}
                  error={!!bulkAddForm.formState.errors.amount}
                  helperText={bulkAddForm.formState.errors.amount?.message}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Category</InputLabel>
                  <Controller
                    name="category"
                    control={bulkAddForm.control}
                    render={({ field }) => (
                      <Select {...field} label="Category">
                        <MenuItem value="dues">Membership Dues</MenuItem>
                        <MenuItem value="fees">Administrative Fees</MenuItem>
                        <MenuItem value="event_fee">Event Fee</MenuItem>
                        <MenuItem value="late_payment">Late Payment Penalty</MenuItem>
                        <MenuItem value="other">Other</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Controller
                    name="dueDate"
                    control={bulkAddForm.control}
                    render={({ field }) => (
                      <DatePicker
                        label="Due Date"
                        value={field.value}
                        onChange={field.onChange}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!bulkAddForm.formState.errors.dueDate,
                            helperText: bulkAddForm.formState.errors.dueDate?.message
                          }
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={2}
                  placeholder="e.g., Annual membership fees for 2023"
                  {...bulkAddForm.register('description')}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...bulkAddForm.register('applyToAll')}
                    />
                  }
                  label="Apply to all members (not just selected)"
                />
                <Typography variant="caption" color="text.secondary" display="block">
                  This will assign the debt to all members in the system, not just the ones you've selected.
                </Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkAddDialog(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={loading.action}
            >
              {loading.action ? <CircularProgress size={24} /> : 'Assign Debt'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      
      <Dialog open={bulkEditDialog} onClose={() => setBulkEditDialog(false)} maxWidth="sm" fullWidth>
        <form onSubmit={bulkEditForm.handleSubmit(handleBulkEditSubmit)}>
          <DialogTitle>
            Edit {selectedDebts.length} Debt{selectedDebts.length !== 1 && 's'}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Controller
                    name="status"
                    control={bulkEditForm.control}
                    render={({ field }) => (
                      <Select {...field} label="Status">
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="resolved">Resolved</MenuItem>
                      </Select>
                    )}
                  />
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      {...bulkEditForm.register('applyDueDateToAll')}
                    />
                  }
                  label="Update due date for all selected debts"
                />
              </Grid>
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Controller
                    name="newDueDate"
                    control={bulkEditForm.control}
                    render={({ field }) => (
                      <DatePicker
                        label="New Due Date"
                        value={field.value}
                        onChange={field.onChange}
                        disabled={!bulkEditForm.watch('applyDueDateToAll')}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            helperText: bulkEditForm.watch('applyDueDateToAll') 
                              ? '' 
                              : 'Enable "Update due date" to change this'
                          }
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setBulkEditDialog(false)}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={loading.action}
            >
              {loading.action ? <CircularProgress size={24} /> : 'Update Debts'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      
      <Dialog open={bulkDeleteDialog} onClose={() => setBulkDeleteDialog(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete {selectedDebts.length} debt{selectedDebts.length !== 1 && 's'}? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialog(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            color="error" 
            onClick={handleBulkDelete}
            disabled={loading.action}
          >
            {loading.action ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading.action}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
};

export default AdminDebtManagementPage;
